/**
 * Archive loader — extracts .previs files bundled with thumbnail/video assets
 * from zip archives. The expected structure:
 *
 *   archive.zip
 *   ├── 8675301.previs
 *   └── .8675309/          (hidden folder — dot-prefixed)
 *       ├── 42.jpg         (shot thumbnail, named by shotId)
 *       ├── 57.jpg
 *       ├── 42.mp4         (shot animation video, named by shotId)
 *       └── ...
 */

import { unzipSync } from 'fflate';
import { loadPrevisFile } from './previsLoader.ts';
import { cacheAssetBytes } from './assetStorage.ts';
import type { TimelineProject, VideoShot } from '@/types/timeline.ts';

/** Check if a buffer starts with the PK zip magic bytes */
export function isZipArchive(buffer: ArrayBuffer): boolean {
  const view = new Uint8Array(buffer, 0, 4);
  return view[0] === 0x50 && view[1] === 0x4B && view[2] === 0x03 && view[3] === 0x04;
}

/** Load a zip archive containing a .previs file and optional assets */
export async function loadArchive(buffer: ArrayBuffer): Promise<TimelineProject> {
  const files = unzipSync(new Uint8Array(buffer));

  // Find the .previs file (any path, first match)
  let previsData: Uint8Array | null = null;
  for (const [path, data] of Object.entries(files)) {
    if (path.endsWith('.previs') && data.length > 0) {
      previsData = data;
      break;
    }
  }
  if (!previsData) {
    throw new Error('No .previs file found in archive');
  }

  // Load the project normally
  const project = await loadPrevisFile(previsData.buffer as ArrayBuffer);

  // Find the hidden asset folder (dot-prefixed directory)
  // Collect thumbnails (*.jpg, *.jpeg, *.png) and videos (*.mp4, *.mov)
  const thumbnails = new Map<number, Uint8Array>();
  const videos = new Map<number, { data: Uint8Array; ext: string }>();

  for (const [path, data] of Object.entries(files)) {
    if (data.length === 0) continue;

    // Match files inside a dot-prefixed folder: .XXXXX/shotId.ext
    // The folder can be at root or nested: .folder/file or prefix/.folder/file
    const match = path.match(/(?:^|\/)\.[^/]+\/(\d+)\.(jpg|jpeg|png|mp4|mov)$/i);
    if (!match) continue;

    const shotId = parseInt(match[1], 10);
    const ext = match[2].toLowerCase();

    if (ext === 'jpg' || ext === 'jpeg' || ext === 'png') {
      thumbnails.set(shotId, data);
    } else if (ext === 'mp4' || ext === 'mov') {
      videos.set(shotId, { data, ext });
    }
  }

  // Apply thumbnails to shots that don't already have one from staticimagedata
  if (thumbnails.size > 0) {
    for (const scene of project.scenes) {
      for (const shot of scene.shots) {
        if (!shot.thumbnailDataUrl) {
          const thumbData = thumbnails.get(shot.id);
          if (thumbData) {
            const mimeType = 'image/jpeg'; // Unity exports JPEG
            const blob = new Blob([thumbData as BlobPart], { type: mimeType });
            shot.thumbnailDataUrl = await blobToDataUrlAsync(blob);
          }
        }
      }
    }
  }

  // Apply videos — create blob URLs
  if (videos.size > 0) {
    for (const scene of project.scenes) {
      for (const shot of scene.shots) {
        const videoEntry = videos.get(shot.id);
        if (videoEntry) {
          const mimeType = videoEntry.ext === 'mov' ? 'video/quicktime' : 'video/mp4';
          const copy = new Uint8Array(videoEntry.data);
          const blob = new Blob([copy], { type: mimeType });
          const objectUrl = URL.createObjectURL(blob);
          cacheAssetBytes(objectUrl, copy);
          shot.videoObjectUrl = objectUrl;

          // Also add to videoShots array if not already present
          if (!project.videoShots.some(v => v.shotId === shot.id)) {
            project.videoShots.push({
              shotId: shot.id,
              objectUrl,
              mimeType,
            } as VideoShot);
          }
        }
      }
    }
  }

  return project;
}

/** Convert a Blob to a data URL using FileReader */
function blobToDataUrlAsync(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
